import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Video, Calendar, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSessionsPage = () => {
    const { language } = useLanguage();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        title: '', title_en: '', description: '', description_en: '',
        category: 'weekly_review', video_url: '', thumbnail_url: '',
        duration_minutes: 60, session_date: ''
    });

    const categories = [
        { value: 'weekly_review', label: 'Weekly Review' },
        { value: 'qa_replay', label: 'Q&A Replay' }
    ];

    // Ensure auth token is set on component mount
    useEffect(() => {
        const token = localStorage.getItem('nusatrade_token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, []);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            const response = await axios.get(`${API}/live-sessions`);
            setSessions(response.data);
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (!token) {
                toast.error(language === 'id' ? 'Sila login semula' : 'Please login again');
                return;
            }
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            
            if (editing) {
                await axios.put(`${API}/admin/live-sessions/${editing.id}`, form, config);
                toast.success(language === 'id' ? 'Session diupdate!' : 'Session updated!');
            } else {
                await axios.post(`${API}/admin/live-sessions`, form, config);
                toast.success(language === 'id' ? 'Session ditambahkan!' : 'Session added!');
            }
            setDialogOpen(false);
            setEditing(null);
            resetForm();
            fetchSessions();
        } catch (error) {
            console.error('Save session error:', error);
            toast.error(language === 'id' ? 'Gagal simpan session' : 'Error saving session');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus session ini?' : 'Delete this session?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            await axios.delete(`${API}/admin/live-sessions/${id}`, config);
            toast.success(language === 'id' ? 'Session dihapus!' : 'Session deleted!');
            fetchSessions();
        } catch (error) {
            toast.error('Error deleting session');
        }
    };

    const resetForm = () => {
        setForm({
            title: '', title_en: '', description: '', description_en: '',
            category: 'weekly_review', video_url: '', thumbnail_url: '',
            duration_minutes: 60, session_date: new Date().toISOString().split('T')[0]
        });
    };

    const openEdit = (session) => {
        setEditing(session);
        setForm({
            title: session.title || '',
            title_en: session.title_en || '',
            description: session.description || '',
            description_en: session.description_en || '',
            category: session.category || 'weekly_review',
            video_url: session.video_url || '',
            thumbnail_url: session.thumbnail_url || '',
            duration_minutes: session.duration_minutes || 60,
            session_date: session.session_date || ''
        });
        setDialogOpen(true);
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-white">
                            {language === 'id' ? 'Kelola Live Sessions' : 'Manage Live Sessions'}
                        </h1>
                        <p className="text-slate-400 mt-1">{sessions.length} sessions</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => { setEditing(null); resetForm(); }} data-testid="add-session-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'id' ? 'Tambah Session' : 'Add Session'}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>
                                    {editing ? (language === 'id' ? 'Edit Session' : 'Edit Session') : (language === 'id' ? 'Tambah Session' : 'Add Session')}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Title (ID)</Label>
                                        <Input
                                            value={form.title}
                                            onChange={(e) => setForm({...form, title: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                            data-testid="session-title"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Title (EN)</Label>
                                        <Input
                                            value={form.title_en}
                                            onChange={(e) => setForm({...form, title_en: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description (ID)</Label>
                                    <Textarea
                                        value={form.description}
                                        onChange={(e) => setForm({...form, description: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                        data-testid="session-description"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Description (EN)</Label>
                                    <Textarea
                                        value={form.description_en}
                                        onChange={(e) => setForm({...form, description_en: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Category</Label>
                                        <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                                            <SelectTrigger className="bg-slate-700 border-slate-600">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-700 border-slate-600">
                                                {categories.map(c => (
                                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Session Date</Label>
                                        <Input
                                            type="date"
                                            value={form.session_date}
                                            onChange={(e) => setForm({...form, session_date: e.target.value})}
                                            className="bg-slate-700 border-slate-600"
                                            data-testid="session-date"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Duration (min)</Label>
                                        <Input
                                            type="number"
                                            value={form.duration_minutes}
                                            onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value) || 60})}
                                            className="bg-slate-700 border-slate-600"
                                            data-testid="session-duration"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Video URL</Label>
                                    <Input
                                        value={form.video_url}
                                        onChange={(e) => setForm({...form, video_url: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                        placeholder="https://youtube.com/..."
                                        data-testid="session-video-url"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Thumbnail URL</Label>
                                    <Input
                                        value={form.thumbnail_url}
                                        onChange={(e) => setForm({...form, thumbnail_url: e.target.value})}
                                        className="bg-slate-700 border-slate-600"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-slate-700 border-slate-600">
                                        {language === 'id' ? 'Batal' : 'Cancel'}
                                    </Button>
                                    <Button onClick={handleSave} data-testid="save-session-btn">
                                        {language === 'id' ? 'Simpan' : 'Save'}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Sessions List */}
                <div className="grid gap-4">
                    {sessions.length === 0 ? (
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="py-12 text-center">
                                <Video className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <p className="text-slate-400">{language === 'id' ? 'Belum ada session' : 'No sessions yet'}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        sessions.map((session) => (
                            <Card key={session.id} className="bg-slate-800 border-slate-700" data-testid={`session-${session.id}`}>
                                <CardContent className="py-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4 flex-1">
                                            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                                <Video className="w-6 h-6 text-cyan-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold text-white truncate">{session.title}</h3>
                                                    <span className="px-2 py-0.5 rounded text-xs bg-cyan-500/20 text-cyan-400">
                                                        {categories.find(c => c.value === session.category)?.label || session.category}
                                                    </span>
                                                </div>
                                                <p className="text-slate-400 text-sm line-clamp-2">{session.description}</p>
                                                <div className="flex items-center gap-4 mt-2">
                                                    <p className="text-slate-500 text-xs flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {session.session_date || 'No date'}
                                                    </p>
                                                    <p className="text-slate-500 text-xs flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {session.duration_minutes || 60} min
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => openEdit(session)}
                                                className="text-slate-400 hover:text-white"
                                                data-testid={`edit-session-${session.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => handleDelete(session.id)}
                                                className="text-slate-400 hover:text-red-400"
                                                data-testid={`delete-session-${session.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};
